import React, { useState, useMemo } from 'react'
import PropTypes from 'prop-types'
import { Button } from '../../ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../ui/card'
import { Input } from '../../ui/input'
import { Label } from '../../ui/label'
import { BookOpen, Check, Plus, ArrowLeft, Search } from 'lucide-react'
import { cn } from '../../../lib/utils'
import { SPORT_OPTIONS } from '../../../utilities/sportOptions'

function CurriculumSelection({ curriculums, selectedIds, facilityName, onSubmit, onCreateCurriculum }) {
  const [step, setStep] = useState('sport')
  const [selectedSport, setSelectedSport] = useState(null)
  const [customSportName, setCustomSportName] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [selected, setSelected] = useState(selectedIds || [])
  const [showCreateForm, setShowCreateForm] = useState(false)
  const [newCurriculumName, setNewCurriculumName] = useState('')

  const filteredSports = useMemo(() => {
    if (!searchQuery) return SPORT_OPTIONS
    return SPORT_OPTIONS.filter((sport) => sport.label.toLowerCase().includes(searchQuery.toLowerCase()))
  }, [searchQuery])

  const filteredCurriculums = useMemo(() => {
    if (!selectedSport || selectedSport === 'other') return []
    return curriculums.filter((c) => c.sport === selectedSport)
  }, [curriculums, selectedSport])

  function handleSportSelect(sport) {
    setSelectedSport(sport)
    if (sport === 'other') {
      setStep('other-input')
    } else {
      setStep('curriculum')
      setNewCurriculumName(`${facilityName} ${getSportLabel(sport)} Curriculum`)
    }
  }

  function handleOtherSportSubmit() {
    if (!customSportName.trim()) return
    setStep('curriculum')
    setNewCurriculumName(`${facilityName} ${customSportName} Curriculum`)
  }

  function handleToggle(curriculumId) {
    setSelected((prev) => {
      if (prev.includes(curriculumId)) {
        return prev.filter((id) => id !== curriculumId)
      }
      return [...prev, curriculumId]
    })
  }

  async function handleSubmitExisting() {
    setIsSubmitting(true)
    try {
      await onSubmit(selected)
    } catch (error) {
      setIsSubmitting(false)
    }
  }

  async function handleCreateSubmit() {
    if (!newCurriculumName.trim()) return
    setIsSubmitting(true)
    try {
      await onCreateCurriculum({
        name: newCurriculumName,
        sport: selectedSport,
        customSportName: selectedSport === 'other' ? customSportName : null
      })
    } catch (error) {
      setIsSubmitting(false)
    }
  }

  function getSportLabel(sport) {
    const option = SPORT_OPTIONS.find((s) => s.value === sport)
    return option ? option.label : sport
  }

  function goBack() {
    if (step === 'curriculum') {
      if (selectedSport === 'other') {
        setStep('other-input')
      } else {
        setStep('sport')
        setSelectedSport(null)
      }
    } else if (step === 'other-input') {
      setStep('sport')
      setSelectedSport(null)
      setCustomSportName('')
    }
    setShowCreateForm(false)
    setSelected([])
  }

  if (step === 'sport') {
    return (
      <div className="flex items-center justify-center min-h-[80vh] px-4 py-8">
        <Card className="w-full max-w-4xl">
          <CardHeader className="space-y-1 text-center">
            <div className="mx-auto w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
              <BookOpen className="w-6 h-6 text-purple-600" />
            </div>
            <CardTitle className="text-2xl font-bold">What sport do you teach?</CardTitle>
            <CardDescription>Select the primary sport your facility focuses on</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Search sports..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3 max-h-[400px] overflow-y-auto">
                {filteredSports.map((sport) => (
                  <button
                    key={sport.value}
                    type="button"
                    onClick={() => handleSportSelect(sport.value)}
                    className={cn(
                      'flex items-center justify-center p-4 rounded-lg border-2 transition-all hover:border-purple-300 hover:bg-purple-50',
                      'border-gray-200 bg-white'
                    )}
                  >
                    <span className="text-sm font-medium text-gray-700 text-center">{sport.label}</span>
                  </button>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (step === 'other-input') {
    return (
      <div className="flex items-center justify-center min-h-[80vh] px-4 py-8">
        <Card className="w-full max-w-lg">
          <CardHeader className="space-y-1 text-center">
            <div className="mx-auto w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
              <BookOpen className="w-6 h-6 text-purple-600" />
            </div>
            <CardTitle className="text-2xl font-bold">What sport do you teach?</CardTitle>
            <CardDescription>Tell us about your sport so we can set things up properly</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="customSport">Sport Name</Label>
                <Input
                  id="customSport"
                  placeholder="e.g., Archery, Curling, Field Hockey..."
                  value={customSportName}
                  onChange={(e) => setCustomSportName(e.target.value)}
                  autoFocus
                />
              </div>

              <div className="flex gap-3">
                <Button variant="outline" onClick={goBack} className="flex-1">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back
                </Button>
                <Button
                  onClick={handleOtherSportSubmit}
                  disabled={!customSportName.trim()}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  Continue
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    )
  }

  const sportDisplayName = selectedSport === 'other' ? customSportName : getSportLabel(selectedSport)
  const hasCurriculums = filteredCurriculums.length > 0

  return (
    <div className="flex items-center justify-center min-h-[80vh] px-4 py-8">
      <Card className="w-full max-w-3xl">
        <CardHeader className="space-y-1 text-center">
          <div className="mx-auto w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
            <BookOpen className="w-6 h-6 text-purple-600" />
          </div>
          <CardTitle className="text-2xl font-bold">
            {showCreateForm ? 'Create Your Curriculum' : `${sportDisplayName} Curriculum`}
          </CardTitle>
          <CardDescription>
            {showCreateForm
              ? 'Give your curriculum a name. You can add skill levels later.'
              : hasCurriculums
              ? 'Select an existing curriculum or create your own'
              : 'Create a custom curriculum for your program'}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-6">
            {!showCreateForm && hasCurriculums && (
              <>
                <div className="grid grid-cols-1 gap-4">
                  {filteredCurriculums.map((curriculum) => {
                    const isSelected = selected.includes(curriculum.id)
                    return (
                      <button
                        key={curriculum.id}
                        type="button"
                        onClick={() => handleToggle(curriculum.id)}
                        className={cn(
                          'relative flex items-start p-4 rounded-lg border-2 text-left transition-all',
                          isSelected
                            ? 'border-purple-600 bg-purple-50'
                            : 'border-gray-200 hover:border-purple-300 hover:bg-gray-50'
                        )}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="font-medium text-gray-900">{curriculum.name}</div>
                          <div className="text-sm text-gray-500">Pre-built curriculum with skill levels</div>
                        </div>
                        <div
                          className={cn(
                            'flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all',
                            isSelected ? 'bg-purple-600 border-purple-600' : 'border-gray-300'
                          )}
                        >
                          {isSelected && <Check className="w-3 h-3 text-white" />}
                        </div>
                      </button>
                    )
                  })}
                </div>

                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <span className="w-full border-t" />
                  </div>
                  <div className="relative flex justify-center text-xs uppercase">
                    <span className="bg-white px-2 text-gray-500">Or create your own</span>
                  </div>
                </div>
              </>
            )}

            {!showCreateForm && (
              <button
                type="button"
                onClick={() => setShowCreateForm(true)}
                className="w-full flex items-center justify-center p-4 rounded-lg border-2 border-dashed border-gray-300 hover:border-purple-300 hover:bg-purple-50 transition-all"
              >
                <Plus className="w-5 h-5 mr-2 text-purple-600" />
                <span className="font-medium text-purple-600">Create Custom Curriculum</span>
              </button>
            )}

            {showCreateForm && (
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <div className="space-y-2">
                  <Label htmlFor="curriculumName">Curriculum Name</Label>
                  <Input
                    id="curriculumName"
                    value={newCurriculumName}
                    onChange={(e) => setNewCurriculumName(e.target.value)}
                    placeholder={`${facilityName} ${sportDisplayName} Curriculum`}
                    autoFocus
                  />
                </div>
                <p className="text-sm text-gray-500">
                  You can add skill levels and customize your curriculum after setup.
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <Button
                variant="outline"
                onClick={showCreateForm ? () => setShowCreateForm(false) : goBack}
                className="flex-shrink-0"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back
              </Button>

              {showCreateForm ? (
                <Button
                  onClick={handleCreateSubmit}
                  disabled={isSubmitting || !newCurriculumName.trim()}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  {isSubmitting ? (
                    <>
                      <div className="mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Creating...
                    </>
                  ) : (
                    'Create & Continue'
                  )}
                </Button>
              ) : (
                <Button
                  onClick={handleSubmitExisting}
                  disabled={isSubmitting}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                >
                  {isSubmitting ? (
                    <>
                      <div className="mr-2 h-4 w-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      Saving...
                    </>
                  ) : selected.length === 0 ? (
                    'Skip for now'
                  ) : (
                    `Continue with ${selected.length} curriculum${selected.length !== 1 ? 's' : ''}`
                  )}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}

CurriculumSelection.propTypes = {
  curriculums: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      name: PropTypes.string.isRequired,
      sport: PropTypes.string.isRequired
    })
  ).isRequired,
  selectedIds: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.string, PropTypes.number])),
  facilityName: PropTypes.string,
  onSubmit: PropTypes.func.isRequired,
  onCreateCurriculum: PropTypes.func.isRequired
}

export default CurriculumSelection
